// Token storage + OAuth bounce. Same pattern the app UIs use, just pointed
// at the platform's auth context (`app=platform` on the /start/google call).
//
// Token lifecycle:
//   - Google OAuth bounces back to us with BOTH tokens in the URL fragment:
//       #access_token=… &refresh_token=… &expires_in=900 &user=…
//   - The access token is short-lived (15 min by default in auth-service).
//   - The refresh token is long-lived (~30 days) and is exchanged at
//     POST /token/refresh for a fresh pair when the access token expires.
//   - api.ts wraps every fetch and, on 401, transparently refreshes +
//     retries before giving up and bouncing back to /login. So the
//     operator stays signed in continuously as long as their refresh
//     token is still valid.
import { type RefreshFailure } from "./lib/session_recovery";
import { loginUrl } from "./lib/login_url";

const PLATFORM_BASE = (import.meta.env.VITE_PLATFORM_BASE ?? "http://127.0.0.1:5900").replace(/\/$/, "");
const AUTH_BASE     = (import.meta.env.VITE_AUTH_BASE     ?? "http://127.0.0.1:4100").replace(/\/$/, "");

const ACCESS_KEY  = "platform_token";          // legacy name kept for storage compat
const REFRESH_KEY = "platform_refresh_token";

export const getToken         = ()                       => localStorage.getItem(ACCESS_KEY);
export const setToken         = (t: string | null)       => t ? localStorage.setItem(ACCESS_KEY,  t) : localStorage.removeItem(ACCESS_KEY);
export const getRefreshToken  = ()                       => localStorage.getItem(REFRESH_KEY);
export const setRefreshToken  = (t: string | null)       => t ? localStorage.setItem(REFRESH_KEY, t) : localStorage.removeItem(REFRESH_KEY);

export const getUserId = () => {
  const t = getToken(); if (!t) return null;
  try { return JSON.parse(atob(t.split(".")[1])).sub as string; } catch { return null; }
};
// `getEmail` removed 2026-09-23 with the nav-bar e-mail that was its only caller. The Account page
// reads the address from the server (`v.email`), which is the right source: a claim decoded from a token
// in the browser is whatever the token said when it was issued. Nothing here needs the token's copy.

// Auth-service starts Google OAuth and bounces back to `return` with both
// the access + refresh tokens in the URL fragment (fragments are
// browser-only, never sent to a server).
//
// `cfToken` est le jeton du contrôle anti-robot, quand il y en a un. Absent, il ne s'écrit PAS
// dans l'adresse — un `cf=` vide dirait au serveur « voici un jeton » en lui tendant le vide, et
// il refuserait. La construction de l'adresse vit dans lib/login_url, éprouvée.
export function login(returnTo = location.pathname, cfToken?: string | null): void {
  location.href = loginUrl({
    authBase: AUTH_BASE,
    app: "platform",
    returnTo: `${location.origin}${returnTo}`,
    cfToken,
  });
}

export function logout(): void {
  setToken(null);
  setRefreshToken(null);
  // Best-effort: revoke server-side. Fire-and-forget; the local clear
  // above is the source of truth for "logged out" on this device.
  // (Don't await — we want logout to be instant.)
  // Skip if refresh token was already cleared.
  location.href = "/";
}

export function captureTokenFromFragment(): void {
  if (!location.hash || location.hash.length < 2) return;
  const params  = new URLSearchParams(location.hash.slice(1));
  const access  = params.get("access_token");
  const refresh = params.get("refresh_token");
  if (access)  setToken(access);
  if (refresh) setRefreshToken(refresh);
  if (access || refresh) {
    // Clean the fragment from the URL bar so the tokens don't sit in
    // browser history / get copy-pasted by accident.
    history.replaceState(null, "", location.pathname + location.search);
  }
}

// Exchange the refresh token for a fresh access+refresh pair.
//
// ── L'ÉCHEC DIT LEQUEL IL EST, ET C'ÉTAIT LE DÉFAUT ─────────────────────────
//
// Cette fonction rendait `string | null` : UNE façon d'échouer pour deux situations qui n'ont
// rien à voir. Le serveur qui refuse veut dire « la session est morte » ; ne pas pouvoir
// demander ne dit rien du tout. Le `catch` ci-dessous prenait déjà soin de ne PAS effacer les
// jetons — mais l'appelant voyait le 401 initial toujours debout et appelait `logout()`, qui les
// efface : sa précaution était annulée. Une coupure réseau d'une seconde détruisait donc une
// session de trente jours. La décision vit maintenant dans lib/session_recovery.ts, pure et
// éprouvée, et les trois sites d'appel la consultent au lieu d'en garder une copie.
//
// Concurrent calls coalesce: if a refresh is already in flight, all
// callers await the same promise. Otherwise N parallel 401s would
// trigger N refreshes, and rotation would invalidate all but one.
export type RefreshResult =
  | { ok: true;  access: string }
  | { ok: false; failure: RefreshFailure };

let refreshInFlight: Promise<RefreshResult> | null = null;

export function refreshAccessToken(): Promise<RefreshResult> {
  if (refreshInFlight) return refreshInFlight;
  const rt = getRefreshToken();
  if (!rt) return Promise.resolve({ ok: false, failure: "no-token" });
  refreshInFlight = (async (): Promise<RefreshResult> => {
    try {
      const r = await fetch(`${AUTH_BASE}/token/refresh`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ refresh_token: rt }),
      });
      if (!r.ok) {
        // Refresh failed (expired / revoked / reused). Clear local
        // state so the next render bounces to /login cleanly.
        setToken(null);
        setRefreshToken(null);
        return { ok: false, failure: "refused" };
      }
      const j = await r.json() as { access_token: string; refresh_token: string };
      setToken(j.access_token);
      setRefreshToken(j.refresh_token);
      return { ok: true, access: j.access_token };
    } catch {
      // On n'a pas pu DEMANDER : réseau coupé, service qui redémarre, ou réponse bloquée par le
      // navigateur (une origine non autorisée, la panne du 2026-09-29). Les jetons RESTENT — et
      // c'est maintenant l'appelant qui respecte ce choix, parce qu'il le lit dans la réponse.
      return { ok: false, failure: "unreachable" };
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export const platformBase = PLATFORM_BASE;
/** Le service d'identité. L'écran de connexion lui demande la clé publique du contrôle. */
export const authBase = AUTH_BASE;
